import { instagram } from '@round/api';
import moment from 'moment';
import { useSong, useMusicSong } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramAudios, useInstagramTimeSeries } from '@round/platforms/instagram';
import { getMergedAudioIdsFromSongData } from '../../helpers';

type Params = {
    songId?: number | null;
    page?: number;
    page_size?: number;
    ordering?: instagram.GetInstagramAudiosOrderingParam;
};

export default function useInstagramTopAudios({ songId, ...rest }: Params) {
    const today = moment();
    const twoWeeksAgo = moment().subtract(14, 'days');

    const { data: song, isLoading: isSongLoading } = useSong(songId);
    const { data: musicSong, isLoading: isMusicSongLoading } = useMusicSong(song?.music_song_id);

    const { instagramAudioIds: audioIds } = getMergedAudioIdsFromSongData(song, musicSong);

    const {
        data: audiosData,
        isLoading: isAudiosLoading,
        isFetching,
        isError,
        error,
    } = useInstagramAudios(
        { ...rest, id: audioIds.join(',') },
        {
            enabled: !!song && !isMusicSongLoading && audioIds.length > 0,
            // preserve count on pagination/ordering changes
            placeholderData: (previousData) => (previousData ? { ...previousData, results: [] } : undefined),
        }
    );

    const { data: timeSeries, isLoading: isTimeSeriesLoading } = useInstagramTimeSeries(
        {
            type: 'audio',
            fields: ['posts_daily_change'],
            ids: audioIds,
            start_date: twoWeeksAgo.format('YYYY-MM-DD'),
            end_date: today.format('YYYY-MM-DD'),
        },
        { enabled: !!song && !isMusicSongLoading && audioIds.length > 0 }
    );

    const data = audiosData
        ? {
              audios: audiosData,
              timeSeries: timeSeries ?? null,
          }
        : null;

    return {
        data,
        isLoading: isSongLoading || isMusicSongLoading || isAudiosLoading || isTimeSeriesLoading,
        isFetching,
        isError,
        error,
    };
}
